import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
    useGridApiRef,
    gridRowSelectionIdsSelector,
    gridRowTreeSelector
 } from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import moment from 'moment';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Button } from "@mui/material";
import Tooltip from '@mui/material/Tooltip';
import ReviewMMT from './ReviewMMT';
import ToggleButton from './ToggleButton';
import BulkDownloadButton from '../../container/SupportCoordinator/BulkDownloadButton'
import './SupportCoordinatorManagement.css';
import {APIURL} from './../../config'
import axios from 'axios';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { NavLink } from "react-router-dom";
import ControlledDataGrid from '../../components/DataGrid/ControlledDataGrid';
import { Call, Home, Clear, Timelapse, Person } from '@mui/icons-material';
import { ReactComponent as FaceToFace } from './../SupportCoordinator/facetoface.svg';
import SvgIcon from '@mui/material/SvgIcon';
import JSZip from 'jszip';
import { AnalyticsBrowser } from '@segment/analytics-next'
import { SEGMENTAPIKEY } from '../../config';
import BulkDownload from '../SupportCoordinator/Modals/BulkDownload';
import BulkApproveButton from './BulkApproveButton';

const analytics = AnalyticsBrowser.load({ writeKey: SEGMENTAPIKEY })


function SupprotCoordinatorManagement(props) {
    const [showReview, setShowReview] = useState(false);
    const [reviewData, setReviewData] = useState({});

    const [updated, setUpdated] = useState(false);
    const [error, setError] = useState(false);

    // month1 oldest (11 months ago) -> month12 current
    const [month, setMonth] = useState('month12');

    const [rowHeight, setRowHeight] = useState(40);

    const [, setEmployees] = useState([]);
    const [, setProjects] = useState([]);
    const employeesRef = useRef([]);
    const projectsRef = useRef([]);
    const employeeMapRef = useRef(new Map());
    const projectMapRef = useRef(new Map());

    const [isGrouped, setIsGrouped] = useState(true);
    const [gridId, setGridId] = useState(52);
    const apiRef = useGridApiRef();
    const expansionState = useRef({});
    const [monthlyTemplate, setMonthlyTemplate] = useState(null);
    const [quarterlyTemplate, setQuarterlyTemplate] = useState(null);
    const [isBulkDownloadLoading, setIsBulkDownloadLoading] = useState(false);
    const [bulkApproveMode, setBulkApproveMode] = useState(false);
    const [isBulkApproving, setIsBulkApproving] = useState(false);

    // Create data source for server-side tree data
    const dataSource = useMemo(() => ({
        getRows: async (params) => {
            try {
                console.log('=== DataSource getRows called ===');
                console.log('Full params object:', params);
                console.log('groupKeys:', params.groupKeys);
                console.log('groupKeys type:', typeof params.groupKeys);
                console.log('groupKeys length:', params.groupKeys?.length);
                console.log('Is initial load:', !params.groupKeys || params.groupKeys.length === 0);
                console.log('Is expansion:', params.groupKeys && params.groupKeys.length > 0);
                
                // Build query parameters for server-side lazy loading
                const queryParams = {
                    comp_id: props.comp_id,
                    emp_id: props.user.emp_id,
                    start: moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'),
                    end: moment().endOf('month').format('YYYY-MM-DDT23:59:59'),
                    paginationModel: JSON.stringify(params.paginationModel || { page: 0, pageSize: 100 }),
                    groupKeys: JSON.stringify(params.groupKeys || []),
                    filterModel: JSON.stringify(params.filterModel || { items: [] }),
                    sortModel: JSON.stringify(params.sortModel || []),
                    mode: isGrouped ? 'tree' : 'flat',
                    month: month
                };

                console.log('Query params being sent:', queryParams);

                const response = await axios.get(APIURL + "/monthlymonitoring/manager/data", {
                    params: queryParams
                });

                console.log('Server response:', response.data);
                
                if (!response.data || !response.data.rows) {
                    console.error('Invalid server response structure:', response.data);
                    return { rows: [], rowCount: 0 };
                }
                
                console.log('Processing rows from server response...', response.data.rows);
                
                
                // Transform server response to match expected format
                const rows = response.data.rows.map((row, index) => {    
                    
                    
                    if (row.type === 'employee') {
                        // Support coordinator row
                        const groupRow = {
                            id: row.id,
                            emp_name: row.emp_name,
                            childrenCount: row.descendantCount || 0,
                            isGroup: true,
                            emp_id: row.emp_id,
                            emp_first_name: row.emp_first_name,
                            emp_last_name: row.emp_last_name,
                            // Add all other required fields with default values to prevent errors
                            ddd_id: null,
                            mt_period: null,
                            review_mt: null,
                            contact_details: null,
                            service_plan: null,
                            program: null,
                            plan_approval_date: null,
                            plan_start_date: null,
                            plan_end_date: null,
                            last_annual_visit: null,
                            last_mt_upload: null
                        };
                        return groupRow;
                    } else {
                        // Customer row
                        const resolveCoordinatorDetails = () => {
                            if (row.emp_name) {
                                return { name: row.emp_name, id: row.emp_id };
                            }

                            if (row.emp_first_name || row.emp_last_name) {
                                const derivedName = `${row.emp_first_name ?? ''} ${row.emp_last_name ?? ''}`.trim();
                                if (derivedName) {
                                    return { name: derivedName, id: row.emp_id };
                                }
                            }

                            if (row.pro_id) {
                                const project = projectMapRef.current.get(row.pro_id);
                                const assignedEmployees = project?.empAssn || [];
                                const resolvedNames = [];
                                let resolvedId;

                                assignedEmployees.forEach((assignment) => {
                                    if (!assignment) {
                                        return;
                                    }
                                    const employeeId = assignment.id ?? assignment.emp_id ?? assignment.empId;
                                    if (employeeId && !resolvedId) {
                                        resolvedId = employeeId;
                                    }

                                    const employeeRecord = employeeMapRef.current.get(employeeId);
                                    const assignmentName = assignment.employee_name || assignment.emp_name;
                                    const recordName = employeeRecord?.employee_name;
                                    const name = recordName || assignmentName;

                                    if (name) {
                                        resolvedNames.push(name);
                                    }
                                });

                                if (resolvedNames.length) {
                                    return { name: Array.from(new Set(resolvedNames)).join(', '), id: resolvedId };
                                }
                            }

                            const monthKeys = Array.from({ length: 12 }, (_, index) => `month${index + 1}`);
                            for (const key of monthKeys) {
                                const monthEntries = row[key];
                                if (!Array.isArray(monthEntries)) {
                                    continue;
                                }

                                for (const task of monthEntries) {
                                    if (!task) {
                                        continue;
                                    }
                                    if (task.emp_name) {
                                        return { name: task.emp_name, id: task.employee_id };
                                    }

                                    const taskEmployee = employeeMapRef.current.get(task.employee_id);
                                    if (taskEmployee?.employee_name) {
                                        return { name: taskEmployee.employee_name, id: task.employee_id };
                                    }
                                }
                            }

                            return { name: '', id: undefined };
                        };

                        const coordinatorDetails = resolveCoordinatorDetails();

                        const monthlyTasksForSelectedPeriod = row[month] || row.month12 || [];
                        let mtContactDate = null;
                        const taskWithMonitoring = monthlyTasksForSelectedPeriod.find((task) => task?.tas_mm_id);
                        if (taskWithMonitoring) {
                            const rawMonitoringData = taskWithMonitoring.monthlyMonitoring?.[0]?.data;
                            if (rawMonitoringData) {
                                try {
                                    const parsedMonitoringData = typeof rawMonitoringData === 'string'
                                        ? JSON.parse(rawMonitoringData)
                                        : rawMonitoringData;
                                    mtContactDate = parsedMonitoringData?.section_1?.k?.answer ?? null;
                                } catch (monitoringParseError) {
                                    // Ignore malformed Monthly Monitoring payloads
                                }
                            }
                        }

                        const customerRow = {
                            // Only provide hierarchy when grouped
                            ...(isGrouped ? { hierarchy: [row.parent_id, row.cus_id] } : {}),
                            id: row.cus_id,
                            cus_id: row.cus_id,
                            cus_name: row.cus_name,
                            customers: row.cus_name,
                                    emp_name: coordinatorDetails.name,
                                    emp_id: coordinatorDetails.id,
                            ddd_id: row.DDDID,
                            mt_period: moment(),
                            review_mt: {
                                cus_id: row.cus_id,
                                cus_name: row.cus_name,
                                DDDID: row.DDDID,
                                active_plan_version: row.active_plan_version,
                                plan_program_name: row.plan_program_name,
                                plan_approval_date: row.plan_approval_date,
                                pro_starts_on: row.pro_starts_on,
                                pro_ends_on: row.pro_ends_on,
                                LastAnnualVisitDate: row.LastAnnualVisitDate,
                                LastMMTUploadDate: row.LastMMTUploadDate,
                                month1: row.month1 || [],
                                month2: row.month2 || [],
                                month3: row.month3 || [],
                                month4: row.month4 || [],
                                month5: row.month5 || [],
                                month6: row.month6 || [],
                                month7: row.month7 || [],
                                month8: row.month8 || [],
                                month9: row.month9 || [],
                                month10: row.month10 || [],
                                month11: row.month11 || [],
                                month12: row.month12 || []
                            },
                            contact_details: {
                                cus_id: row.cus_id,
                                cus_name: row.cus_name
                            },
                            service_plan: row.active_plan_version,
                            program: row.plan_program_name,
                            plan_approval_date: row.plan_approval_date ? moment(row.plan_approval_date).utc().format('MM/DD/YYYY') : null,
                            plan_start_date: row.pro_starts_on ? moment(row.pro_starts_on).utc().format('MM/DD/YYYY') : null,
                            plan_end_date: row.pro_ends_on ? moment(row.pro_ends_on).utc().format('MM/DD/YYYY') : null,
                            last_annual_visit: row.LastAnnualVisitDate ? moment(row.LastAnnualVisitDate).utc().format('MM/DD/YYYY') : null,
                            last_mt_upload: row.LastMMTUploadDate ? moment(row.LastMMTUploadDate).utc().format('MM/DD/YYYY') : null,
                            mt_contact_date: mtContactDate,
                            emp_is_active: true,
                            isGroup: false,
                            childrenCount: 0
                        };
                        return customerRow;
                    }
                });

                console.log('Transformed rows:', rows);
                console.log('Total row count from server:', props.comp_id, props.user.emp_id, month);
                
                
                return {
                    rows,
                    rowCount: response.data.rowCount || rows.length
                };
            } catch (error) {
                console.error('Error in dataSource.getRows:', error);
                console.error('Error details:', error.response?.data || error.message);
                // Return empty result instead of throwing to prevent grid crash
                return { rows: [], rowCount: 0 };
            }
        },
        getGroupKey: (row) => {
            // For support coordinator groups, return the coordinator name or ID
            return row.id;
        },
        getChildrenCount: (row) => {
            // Return the number of children for the row
            return row.childrenCount || 0;
        },
        getRowId: (row) => {
            // Return unique identifier for each row - ESSENTIAL for tree data
            console.log('getRowId called with row:', row);
            return row.id;
        }
    }), [props.comp_id, props.user.emp_id, isGrouped, month]);

    const handleBulkApprove = async () => {
        setIsBulkApproving(true);
        try {
            const rowsMap = gridRowSelectionIdsSelector(apiRef);

            const tasksToApprove = [];
            for (const row of rowsMap.values()) {
                const task = row.review_mt?.[month]?.find(tas => tas.tas_mm_status === 'Waiting For Approval' || tas.tas_mm_status === 'Rejected');
                if (task) {
                    tasksToApprove.push({ row, task });
                }
            }
            if (tasksToApprove.length === 0) {
                setBulkApproveMode(false);
                apiRef.current.setRowSelectionModel({ type: 'include', ids: new Set() });
                return;
            }
            // Approve the monthlymonitoring rows FIRST so a task can never be marked 'Approved'
            // without its linked monthlymonitoring row also being approved/submitted. Previously
            // the per-task status updates ran first and a failure of bulk_approve left tasks
            // 'Approved' with their monthlymonitoring rows still at their default state.
            const taskIds = tasksToApprove.map(({ task }) => task.tas_id);
            await axios.post(APIURL + "/monthlymonitoring/bulk_approve", { task_ids: taskIds });
            for (const { row, task } of tasksToApprove) {
                const sha1 = require('sha1');
                const sessionID = window.localStorage.getItem('Fw4_access_Token');
                const hash = sha1(sessionID);
                const log_data = {
                    emp_id: props.user.emp_id,
                    actionType: 'MT Form Action',
                    actionSubType: 'MT Form approved',
                    actionDetail: task.tas_mm_id,
                    sessionID: hash
                };
                await axios.post(APIURL + "/activity_log", log_data);
                await axios.put(APIURL + "/task/status", {
                    empId: props.user.emp_id,
                    empName: props.user.emp_name,
                    companyId: props.user.comp_id,
                    tas_id: task.tas_id,
                    tas_mm_status: 'Approved',
                    submittedByEmpId: task.employee_id,
                    submittedByEmpName: task.emp_name
                });
            }
            setUpdated(true);
        } catch (err) {
            setError(true);
        } finally {
            // clear selection after approval
            apiRef.current.setRowSelectionModel({ type: 'include', ids: new Set() }) 
            setIsBulkApproving(false);
        }
    };

    const handleShowReview = () => {setShowReview(true)};
    const handleCloseReview = () => setShowReview(false);
    const [loggedToSegment, setLoggedToSegment] = useState(false);

    const [showBulkDownload, setShowBulkDownload] = useState(false);

    const handleShowBulkDownload = () => setShowBulkDownload(true);
    const handleCloseBulkDownload = () => setShowBulkDownload(false);

    const [isChromiumBased, setIsChromiumBased] = useState(false);

    const templateString = props.user.comp_settings.find(setting => setting.feature_id === 53)?.feature_value;
    const isSubscibedToTimeManagement = props.user.subs.find((setting) => setting.feature_id === 12) ? true : false;

    const logToSegment = useCallback(() => {
        if(loggedToSegment) {
            return;
        }
        let page = String(window.location.href).split("/").pop()
        console.log(page,window.location.href)
        if(page.includes("fieldworker") || page === "" || page === "#") {
            page = "Home";
        } else {
            page = String(page).charAt(0).toUpperCase() + String(page).toLowerCase().slice(1);
        }
        console.log(page)
        analytics.page(page + " : SC Management",{
            title:page + " : SC Management",
            path:page + " : SC Management",
        }, {
          userId:String(props.user.emp_id),
        })
        console.log("logged to segment successfully")
        setLoggedToSegment(true);
    }, [loggedToSegment, props.user.emp_id]);

    const syncEmployeeData = useCallback((employeeData = []) => {
        employeesRef.current = employeeData;
        employeeMapRef.current = new Map(
            (employeeData || [])
                .filter(emp => emp && (emp.id !== undefined && emp.id !== null))
                .map(emp => [emp.id, emp])
        );
        setEmployees(employeeData);
    }, [setEmployees]);

    const syncProjectData = useCallback((projectData = []) => {
        projectsRef.current = projectData;
        projectMapRef.current = new Map(
            (projectData || [])
                .filter(proj => proj && (proj.pid !== undefined && proj.pid !== null))
                .map(proj => [proj.pid, proj])
        );
        setProjects(projectData);
    }, [setProjects]);

    const fetchEmployeesAndProjects = useCallback(async ({ force = false } = {}) => {
        if (!force && employeesRef.current.length && projectsRef.current.length) {
            return {
                employees: employeesRef.current,
                projects: projectsRef.current,
            };
        }

        try {
            const [employeesResponse, projectsResponse] = await Promise.all([
                axios.get(APIURL + '/dropdowns/dropdownType/employeeslist', { params: { comp_id: props.comp_id } }),
                axios.get(APIURL + '/dropdowns/dropdownType/projectslist', { params: { comp_id: props.comp_id } })
            ]);

            const employeeData = employeesResponse.data || [];
            const projectData = projectsResponse.data || [];

            syncEmployeeData(employeeData);
            syncProjectData(projectData);
            logToSegment();

            return { employees: employeeData, projects: projectData };
        } catch (error) {
            console.error('Error fetching employees or projects:', error);
            throw error;
        }
    }, [logToSegment, props.comp_id, syncEmployeeData, syncProjectData]);

    const setAlert = (isUpdated) => {
        isUpdated ? setUpdated(true) : setError(false);
    }

    function FaceToFaceIcon(props) {
        return (
          <SvgIcon {...props} viewBox="0 0 256 256" component={FaceToFace}/>
        );
    }

    useEffect(() => {
        fetchEmployeesAndProjects({ force: true }).catch((error) => {
            console.error('Error fetching employees or projects:', error);
        });
    }, [fetchEmployeesAndProjects]);

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                let monthlyRes = await axios.get(APIURL + '/monthlymonitoring/template/monthly', { responseType: 'arraybuffer' });
                let quarterlyRes = await axios.get(APIURL + '/monthlymonitoring/template/quarterly', { responseType: 'arraybuffer' });
                setMonthlyTemplate(monthlyRes.data);
                setQuarterlyTemplate(quarterlyRes.data);
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };
        fetchTemplates();
    }, []);

    useEffect(() => {
        const detectChromium = async () => {
            const userAgent = navigator.userAgent;
            const vendorName = navigator.vendor;
            const isOpera = typeof window.opr !== 'undefined';
            const isFirefox = typeof InstallTrigger !== 'undefined';
            const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
            const isEdgeChromium = userAgent.indexOf('Edg') > -1; // Edge Chromium
            let isBrave = false;
      
            if (navigator.brave && navigator.brave.isBrave) {
              isBrave = await navigator.brave.isBrave();
            }
      
            if (
              (window.chrome && vendorName === 'Google Inc.' && !isOpera && !isEdgeChromium && !isBrave) ||
              isEdgeChromium ||
              isBrave ||
              isOpera
            ) {
              setIsChromiumBased(true);
            }
        };
      
        detectChromium();
      }, []);

    const resolveMtContactDate = useCallback((row) => {
        const parseContactAnswer = (contactAnswer) => {
            if (!contactAnswer) {
                return null;
            }

            const isoMoment = moment(contactAnswer, moment.ISO_8601, true);
            if (isoMoment.isValid()) {
                return isoMoment.toDate();
            }

            const fallbackDate = new Date(contactAnswer);
            return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
        };

        const precomputed = parseContactAnswer(row?.mt_contact_date);
        if (precomputed) {
            return precomputed;
        }

        if (!row?.review_mt) {
            return new Date(0);
        }

        const monthlyTasks = row.review_mt[month] || [];
        for (const tas of monthlyTasks) {
            if (!tas?.tas_mm_id) {
                continue;
            }

            const rawData = tas.monthlyMonitoring?.[0]?.data;
            if (!rawData) {
                continue;
            }

            try {
                const parsedData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
                const parsedContactDate = parseContactAnswer(parsedData?.section_1?.k?.answer);
                if (parsedContactDate) {
                    return parsedContactDate;
                }
            } catch (parseError) {
                // Ignore malformed payloads and continue searching other tasks
            }
        }

        return new Date(0);
    }, [month]);

    // Determine if the given month's MT form should be a Quarterly form.
    // Rule: if the two preceding months' MT forms were both "Monthly" (i.e. an MT was
    // submitted monthly for the last two months), the third month must be Quarterly.
    const isQuarterlyDue = (monthsData, currentMonthIndex) => {
        if (!monthsData) return false;
        const prevIdx = currentMonthIndex - 1;
        const prevPrevIdx = currentMonthIndex - 2;
        if (prevIdx < 1 || prevPrevIdx < 1) return false;
        const collectMt = (arr) => (Array.isArray(arr) ? arr : [])
            .filter(t => t && t.tas_mm_id !== null && t.tas_mm_id !== undefined && Array.isArray(t.monthlyMonitoring))
            .flatMap(t => t.monthlyMonitoring)
            .filter(Boolean);
        const records = [
            ...collectMt(monthsData[`month${prevIdx}`]),
            ...collectMt(monthsData[`month${prevPrevIdx}`])
        ].sort((a, b) => moment(b.created_on).valueOf() - moment(a.created_on).valueOf());
        if (records.length < 2) return false;
        return records[0].mt_type === 'Monthly' && records[1].mt_type === 'Monthly';
    };

    let columns = [
        {
            field: 'customers',
            headerName: 'Customers',
            width: 200,
            hide: false,
            type: "string",
            renderCell: (params) => {
                return (
                    <NavLink
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                textAlign: "center"
                            }}
                            to={{
                                pathname:'/customers',
                                customersProps:{
                                    cus_id: params.row.cus_id,
                                    edit: false
                                }
                            }} 
                        >{params.row.cus_name}</NavLink>
                )
            }
        },
        //{ field: 'cus_id', headerName: 'ID#', width: 100 },
        {
            field: 'mt_period',
            headerName: 'MT Period',
            width: 200,
            hide: false,
            renderCell: (params) => {
                if(!params.value) {
                    return;
                }
                // Derive offset: month12 -> 0 (current), month11 -> 1, ... month1 -> 11
                let numOfMonths = 12 - parseInt(month.replace('month',''));
                return moment(params.value).subtract(numOfMonths, "month").startOf("month").format('MM/DD/YYYY') + ' - ' + moment(params.value).subtract(numOfMonths, "month").endOf("month").format('MM/DD/YYYY');
            }
        },
        {
            field: 'review_mt',
            headerName: 'Review MT',
            width: 200,
            hide: false,
            renderHeader: (params) => {
                const handleChange = (event) => {
                    console.log("aB", event.target.value, month);
                    setMonth(event.target.value);
                    
                }                
                return (
                    <FormControl fullWidth>
                        <Select
                            id="review-mmt-select"
                            value={month}
                            label="Review MMT"
                            onChange={handleChange}
                        >
                            {Array.from({length:12},(_,i)=>12-i).map(mIndex => {
                                const diff = 12 - mIndex;
                                const label = moment().subtract(diff,'month').format('MMM');
                                return (
                                    <MenuItem key={mIndex} value={`month${mIndex}`}>{`MT(${label})`}</MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                )

            },
            renderCell: (params) => {
                let task;
                let status = "Not Submitted";
                let disable = true;
                let className;

                if(!params.value) {
                    return;
                }

                if(params.value.length === 0) {
                    return <Button disabled={true} size="small" variant="text">{status}</Button>;
                }

                const onClick = async (status) => {
                    if (!task?.tas_id) {
                        return;
                    }                    

                    if (!employeesRef.current.length || !projectsRef.current.length) {
                        try {
                            await fetchEmployeesAndProjects();
                        } catch (lookupError) {
                            console.error('Unable to ensure employee/project lookup before opening review:', lookupError);
                        }
                    }

                    let mm;
                    try {
                        mm = await axios.get(APIURL+"/monthlymonitoring/"+ task.tas_id);
                    } catch (fetchErr) {
                        console.error('Unable to fetch monthly monitoring data for review:', fetchErr);
                        setError(true);
                        return;
                    }

                    if (!Array.isArray(mm.data) || mm.data.length === 0) {
                        // Do NOT open the review dialog on an empty/failed result — this can
                        // mean the DB query genuinely errored (see getData) rather than the
                        // task truly having no submission. Silently proceeding here previously
                        // let the review dialog open with undefined mm data.
                        console.error('No monthly monitoring record found for task', task.tas_id);
                        setError(true);
                        return;
                    }

                    const artifactsResponse = await axios.get(APIURL+"/artifacts", { params: { tas_id: task.tas_id } });

                    const artifactsData = Array.isArray(artifactsResponse.data) ? artifactsResponse.data : [];
                    const artifactsWithMeta = artifactsData.map((art) => {
                        const employee = employeeMapRef.current.get(art.employee_id);
                        const project = projectMapRef.current.get(art.project_id);

                        return {
                            ...art,
                            tas_title: task.tas_title,
                            emp_name: employee?.employee_name || art.emp_name || '',
                            pro_title: project?.pname || art.pro_title || ''
                        };
                    });                    

                    const data = {
                        mm: mm.data[0],
                        emp_name: params.row.emp_name,
                        cus_name: params.row.cus_name,
                        id: params.id,
                        status: status === "Approved" || status === "Bulk Approved" ? "Approved" : status === "Rejected" ? "Rejected" : "",
                        artifacts: artifactsWithMeta,
                        task: task,
                        month: month
                    };

                    setReviewData(data);
                    handleShowReview();
                }
                
                params.value[month].map(tas => {
                    if(tas.tas_mm_id && tas.submitted_on) {
                        task = tas;
                        if(tas.tas_mm_status === 'Waiting For Approval') {
                            status = "Review";
                            disable = false;
                        } else if (tas.tas_mm_status === 'Approved') {
                            // if bulk approved, show different label
                            status = tas.bulk_approved ? 'Bulk Approved' : 'Approved';
                            disable = false;
                            className = "approved";
                        } else if (tas.tas_mm_status === 'Rejected') {
                            status = "Rejected";
                            disable = false;
                            className = "rejected";
                        }
                        return;
                    }
                })

                const quarterlyDue = isQuarterlyDue(params.value, parseInt(month.replace('month', '')));
                const reviewButton = (
                    <Button disabled={disable} className={className} onClick={()=> {if(status!=="Not Submitted") onClick(status)}} size="small" variant="text">{status}</Button>
                );

                if (quarterlyDue) {
                    return (
                        <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="A Quarterly MT expected" placement="top">
                                <Box component="span" sx={{ position: 'absolute', top: 4, right: 4, width: 10, height: 10, borderRadius: '50%', backgroundColor: 'red', zIndex: 1 }} />
                            </Tooltip>
                            {reviewButton}
                        </Box>
                    );
                }

                return reviewButton;
            }
        },
        {
            field: 'ddd_id',
            headerName: 'DDD ID',
            width: 140,
            hide: false
        },
        {
            field: 'contact_details',
            headerName: 'Contact Details',
            width: 450,
            hide: false,
            renderCell: (params) => {

                if(!params.row.review_mt) {
                    return <span></span>;
                }

                let phone, address;

                const getIcon = (task) => {
                    let taskType = task.tas_type.toLowerCase();
                    taskType = taskType.includes('visit') && taskType !== 'home visit' ? 'visit' : taskType;
                    switch(taskType) {
                        case 'phone call':
                            phone = task.con_phone?.split(",")[0];
                            return <Call style={{color : "#3FA7D6", marginRight : 10}} />
                        case 'home visit':
                            address = task.address;
                            return <Home style={{color : "#3FA7D6", marginRight : 10}} />
                        case 'visit':
                            address = task.address;
                            return <FaceToFaceIcon style={{ height: "22px", color : "#3FA7D6", marginRight: 10 }} />
                        default:
                            return <Clear style={{color : "red", marginRight : 10, visibility: "hidden"}} />
                    }
                }

                const getDuration = (start, end) => {
                    let duration = moment.duration(moment(end).diff(moment(start)));
                    let days = duration.days();
                    let hours = duration.hours();
                    let minutes = duration.minutes();

                    let durationStr = "";

                    days > 0 && (durationStr += days + "d ");
                    hours > 0 && (durationStr += hours + "h ");
                    minutes > 0 && (durationStr += minutes + "m");

                    return durationStr;
                }

                let task;

                params.row.review_mt[month].map(tas => {
                    if(tas.tas_mm_id) {
                        if(tas.tas_mm_status === 'Submitted' || tas.tas_mm_status === 'Waiting For Approval' ||
                            tas.tas_mm_status === 'Approved' || tas.tas_mm_status === 'Rejected') {
                                task = tas;       
                        }
                    }
                })

                if(!task) {
                    return <span></span>;
                }

                return (
                    <Box onClick={() => {console.log(params)}} sx={{ width: "100%", height: rowHeight }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {getIcon(task)}
                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                width: "100%",
                                height: "100%",
                            }}>
                                <Box sx={{ display: "flex", height: rowHeight * 0.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", minWidth: "100px" }}>
                                        <span>{moment(task.checkoutEmpDate).format('MM/DD/YYYY')}</span>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: 10 }}>{task.tas_title}</span>
                                    </Box>
                                </Box>
                                <Box sx={{ display: "flex", height: rowHeight * 0.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", minWidth: "100px" }}>
                                        <span><Timelapse style={{ color : "#3FA7D6", marginRight : 5 }} fontSize='small' />{getDuration(task.empLocdate, task.checkoutEmpDate)}</span>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {address ? 
                                            <span style={{ marginRight: 10 }}>{address}</span> :
                                            <>
                                                {task.con_name ?
                                                    <>
                                                        <span style={{ lineHeight: "50%", marginRight: 10 }}><Person style={{ color : "#3FA7D6", marginRight : 2 }} fontSize='small' />
                                                            {task.con_name}
                                                        </span>
                                                        <span style={{ lineHeight: "50%", marginRight: 10 }}><Timelapse style={{ color : "#3FA7D6", marginRight : 2 }} fontSize='small' />
                                                            {getDuration(task.startDateofCall, task.endDateofCall)}
                                                        </span>
                                                        <span style={{ marginRight: 10 }}><Call style={{ color : "#3FA7D6", marginRight : 2 }} fontSize='small' />{phone}</span>
                                                    </> :
                                                    <span>No call information found</span>
                                                }
                                            </>
                                        }
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )
            }
        },
        {
            field: 'service_plan',
            headerName: 'Service Plan',
            width: 140,
            hide: false
        },
        {
            field: 'program',
            headerName: 'Program',
            width: 140,
            hide: false
        },
        {
            field: 'mt_submission_date',
            headerName: 'MT Submit Date',
            width: 140,
            hide: false,
            type: "date",
            renderCell: (params) => {
                return (
                    <div>{params.value.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format("MM/DD/YYYY")}</div>
                )
            },
            valueGetter: (value, row, column, apiRef) => {
                let date = new Date(0);

                if(!row.review_mt) {
                    return date;
                }
                row.review_mt[month].map(tas => {
                    if(tas.tas_mm_id) {
                        if(tas.submitted_on)
                            date = new Date(tas.submitted_on);
                    }
                })
                return date;
            },
            valueFormatter: (value, row, column, apiRef) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        },
        {
            field: 'mt_contact_date',
            headerName: 'MT Contact Date',
            width: 150,
            hide: false,
            type: 'date',
            renderCell: (params) => (
                <div>{params.value?.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format('MM/DD/YYYY')}</div>
            ),
            valueGetter: (value, row) => resolveMtContactDate(row),
            valueFormatter: (value) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        },
        {
            field: 'initial_submission_date',
            headerName: 'MT Initial Submit Date',
            width: 160,
            hide: false,
            type: 'date',
            renderCell: (params) => {
                return (
                    <div>{params.value.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format('MM/DD/YYYY')}</div>
                )
            },
            valueGetter: (value, row, column, apiRef) => {
                let date = new Date(0);

                if(!row.review_mt) {
                    return date;
                }
                row.review_mt[month].map(tas => {
                    if(tas.tas_mm_id) {
                        if(tas.initial_submission_date)
                            date = new Date(tas.initial_submission_date);
                    }
                })
                return date;
            },
            valueFormatter: (value, row, column, apiRef) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        },
        {
            field: 'plan_approval_date',
            headerName: 'Plan Approval Date',
            type: "date",
            width: 140,
            hide: false,
            renderCell: (params) => {
                return (
                    <div>{params.value.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format("MM/DD/YYYY")}</div>
                )
            },
            valueGetter: (value, row, column, apiRef) => {
                let date = new Date(0);
                if (value) {
                    date = new Date(value);
                }
                return date;
            },
            valueFormatter: (value, row, column, apiRef) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        },
        {
            field: 'plan_start_date',
            headerName: 'Plan Start Date',
            type: "date",
            width: 140,
            hide: false,
            renderCell: (params) => {
                return (
                    <div>{params.value.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format("MM/DD/YYYY")}</div>
                )
            },
            valueGetter: (value, row, column, apiRef) => {
                let date = new Date(0);
                if (value) {
                    date = new Date(value);
                }
                return date;
            },
            valueFormatter: (value, row, column, apiRef) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        },
        {
            field: 'plan_end_date',
            headerName: 'Plan End Date',
            type: "date",
            width: 140,
            hide: false,
            renderCell: (params) => {
                return (
                    <div>{params.value.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format("MM/DD/YYYY")}</div>
                )
            },
            valueGetter: (value, row, column, apiRef) => {
                let date = new Date(0);
                if (value) {
                    date = new Date(value);
                }
                return date;
            },
            valueFormatter: (value, row, column, apiRef) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        },
        {
            field: 'last_annual_visit',
            headerName: 'Last Annual Visit',
            type: "date",
            width: 140,
            hide: false,
            renderCell: (params) => {
                return (
                    <div>{params.value.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format("MM/DD/YYYY")}</div>
                )
            },
            valueGetter: (value, row, column, apiRef) => {
                let date = new Date(0);
                if (value) {
                    date = new Date(value);
                }
                return date;
            },
            valueFormatter: (value, row, column, apiRef) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        },
        {
            field: 'last_mt_upload',
            headerName: 'Last MT Upload',
            type: "date",
            width: 140,
            hide: false,
            renderCell: (params) => {
                return (
                    <div>{params.value.toDateString() === new Date(0).toDateString() ? '' : moment(params.value).format("MM/DD/YYYY")}</div>
                )
            },
            valueGetter: (value, row, column, apiRef) => {
                let date = new Date(0);
                if (value) {
                    date = new Date(value);
                }
                return date;
            },
            valueFormatter: (value, row, column, apiRef) => value !== new Date(0) ? moment(value).format('MM/DD/YYYY') : null
        }
    ]

    const toolbarButtons = [
        () => (
            <ToggleButton label="Group" value={isGrouped} onClick={() => setIsGrouped(!isGrouped)} />
        ),
        () => (
            <BulkDownloadButton onClick={handleShowBulkDownload} disabled={isBulkDownloadLoading} />
        ),
        () => (
            <BulkApproveButton
                onClick={handleBulkApprove}
                disabled={isBulkApproving}
                isApproving={isBulkApproving}
                bulkApproveMode={bulkApproveMode}
                setBulkApproveMode={setBulkApproveMode}
            />
        )
    ];

    const groupingColDef = useMemo(() => ({
        headerName: 'Support Coordinators',
        width: 300,
        renderHeader: (params) => (<span>{params.colDef.headerName}</span>),
        valueGetter: (value, row) => {            
            if (row?.isGroup) {
                return row.emp_name || row.cus_name || '';
            }
            
            return '';
        }
    }), []);

    const lastBulkDownloadDate = async () => {
        try {
            let res = await axios.get(APIURL+"/activity_log/action", { params: {
                actionType: 'MT Form Action',
                actionSubType: 'MT bulk download',
                employee_id: props.emp_id
            }});

            console.log(res);

            let lastDownloadDate = moment(res.data.data[0].systime);

            return lastDownloadDate;
        } catch (error) {
            console.log(error);
        }

        return null;
    }

    const saveFiles = async (isLastBulkDownload = false) => {    
        try {
            let fileName = props.user.emp_name.replace(' ', '_') + '_' + moment().format('MMM_YYYY') + '_MT_Forms.zip';
            let fileHandle
            if(isChromiumBased) {
                fileHandle = await window.showSaveFilePicker({
                    id: "monthly-monitoring",
                    suggestedName: fileName,
                    types: [{
                        description: 'Zip Files',
                        accept: {
                            'application/zip': ['.zip']
                        }
                    }]
                });
            }
            const createReport = window.createReport;

            let data = await axios.get(APIURL+"/monthlymonitoring/employee/all/"+props.emp_id, { params: { 
                comp_id: props.comp_id,
                created_on: moment().startOf('month').format('YYYY-MM-DD') 
            }});

            if(isLastBulkDownload) {
                let lastDownloadDate = await lastBulkDownloadDate();
                if(lastDownloadDate.isAfter(moment().startOf('month')) && lastDownloadDate.isBefore(moment().endOf('month'))) {
                    data.data = data.data?.filter(mt => moment(mt.last_updated).isAfter(lastDownloadDate));
                }
            }

            let zip = new JSZip();

            let mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

            let createFiles = data.data?.map(async mt => {
                const report = await createReport({
                    template: mt.mt_type === "Monthly" ? monthlyTemplate : quarterlyTemplate,
                    data: JSON.parse(mt.data),
                    cmdDelimiter: ['{', '}'],
                    fixSmartQuotes: true,
                    noSandbox: true,
                    literalXmlDelimiter: '%%%'
                });
                const blob = new Blob([report], { type: mimeType });
                if(templateString) {
                    const values = {
                        customer_name: mt.cus_name.replace(' ', '_'),
                        month: moment(mt.mt_date).utc().format('MMM'),
                        year: moment(mt.mt_date).utc().format('YYYY'),
                        type: mt.mt_type
                    };
                    const replacedString = templateString.replace(/{(.*?)}/g, (match, key) => {
                        return values[key] || match; // Use the value from the 'values' object or keep the placeholder if not found
                    });
                    zip.file(replacedString + '.docx', blob);
                } else {
                    zip.file(mt.cus_name.replace(' ', '_') + '_' + moment(mt.mt_date).format('MMM_YYYY') + `_${mt.mt_type}_Form.docx`, blob);
                }
                await axios.put(APIURL+"/monthlymonitoring/downloaded/" + mt.monthly_monitoring_id);
            })

            await Promise.all(createFiles)
            let blob = await zip.generateAsync({ type: 'blob' })
            
            if(isChromiumBased) {
                const writable = await fileHandle.createWritable();
                await writable.write(blob);
                await writable.close();
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                URL.revokeObjectURL(url);
            }

            var sha1 = require('sha1');
            let sessionID=window.localStorage.getItem('Fw4_access_Token')
            var hash = sha1(sessionID);
            var log_data={
                emp_id: props.emp_id,
                actionType:'MT Form Action',
                actionSubType:'MT bulk download',
                sessionID:hash
            }                                                                                                                                                                                                                                                                 
            let msg = await axios.post(APIURL+"/activity_log", log_data );
        } catch (error) {
            console.log(error);
        }
    }

    const updateTaskStatus = useCallback((id, tas_id, month, status) => {
/*         let row = apiRef.current.getRow(id);

        if(!row) {
            return;
        }

        let updatedRow = { ...row };

        if(!updatedRow.review_mt) {
            return;
        }

        updatedRow.review_mt[month] = updatedRow.review_mt[month].map(tas => {
            if(tas.tas_id === tas_id) {
                return { ...tas, tas_mm_status: status };
            }
            return tas;
        });


        apiRef.current.updateRows([updatedRow]); */
        let rowTree = gridRowTreeSelector(apiRef);
        const parentId = rowTree[id].parent

        apiRef.current?.dataSource.cache.clear();
        apiRef.current?.dataSource?.fetchRows(parentId);
        
    }, [apiRef]);

/*     useEffect(() => {
        if(!apiRef.current?.subscribeEvent) {
            return;
        }        
        apiRef.current.subscribeEvent('rowExpansionChange', (node) => {
            expansionState.current[node.id] = node.childrenExpanded ?? false
        })
    }, [apiRef.current])
    
    const isGroupExpanded = useCallback(
        (node) => expansionState.current[node.id] ?? false,
        [expansionState],
    ) */

    useEffect(() => {
        console.log();
        
        apiRef.current?.dataSource?.cache.clear();
        apiRef.current?.dataSource?.fetchRows();
    }, [isGrouped, apiRef]);
    
    useEffect(() => {
        apiRef.current?.updateColumns(columns);
        apiRef.current?.dataSource?.cache.clear();
        apiRef.current?.dataSource?.fetchRows();
    }, [month]);

    return (
        <Box sx={{ width: '100%', height: "calc(100vh - 150px)" }}>
            <ControlledDataGrid
                checkboxSelection={bulkApproveMode}
                isRowSelectable={(params) => {
                    if (typeof params.id === 'string') return false;
                    if (!bulkApproveMode) return false;
                    const tasks = params.row.review_mt?.[month] || [];
                    return tasks.some(tas => tas.tas_mm_status === 'Waiting For Approval' || tas.tas_mm_status === 'Rejected');
                }}
                gridId={gridId}
                user={props.user}
                treeData={isGrouped}
                groupingColDef={isGrouped ? groupingColDef : undefined}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'review_mt', sort: 'asc' }],
                    }
                }}
                //isGroupExpandedByDefault={isGroupExpanded}
                dataSource={dataSource}
                onDataSourceError={(error, params) => {
                    console.error('DataSource error:', error);
                    setError(true);
                }}
                getRowHeight={useCallback(({ model, densityFactor  }) => {
                    return rowHeight * densityFactor;
                }, [rowHeight])}
                columns={isSubscibedToTimeManagement ? columns : columns.filter(col => col.field !== 'contact_details')}
                pagination={!isGrouped}
                pageSizeOptions={[50, 100, 200]}
                toolbarButtons={toolbarButtons}
                hideFooterSelectedRowCount={true}
                disableRowSelectionOnClick
                loading={props.loading}
                apiRef={apiRef}
                onLoad={(settings) => settings.hasOwnProperty('isGrouped') ? setIsGrouped(settings.isGrouped) : setIsGrouped(true)}
                onSave={(settings) => { settings.isGrouped = isGrouped }}
            />
            <ReviewMMT {...reviewData} show={showReview} handleClose={handleCloseReview} company_id={props.comp_id} user={props.user} reload={props.getEmployees} updateTaskStatus={updateTaskStatus} />
            <BulkDownload show={showBulkDownload} onClose={handleCloseBulkDownload} saveFiles={saveFiles} />
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={updated} autoHideDuration={6000} onClose={()=>{setUpdated(false)}}>
                <MuiAlert onClose={()=>{setUpdated(false)}} severity="success">
                   SUCCESSFULLY UPDATED FORM
                </MuiAlert>
            </Snackbar>
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={error} autoHideDuration={6000} onClose={()=>{setError(false)}}>
                <MuiAlert onClose={()=>{setError(false)}} severity="error">
                    ERROR OCCURRED WHILE UPDATING FORM
                </MuiAlert>
            </Snackbar>
      </Box>
    )
}

export default SupprotCoordinatorManagement;